import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AppMenuitem } from '@/layout/components/app.menuitem';
import Keycloak from 'keycloak-js';
import { appRoutes } from 'src/app.routes';
import { buildRouteRolesMap } from '@/shared/utils/route-permissions.util';

interface MenuItem {
    label?: string;
    icon?: string;
    routerLink?: string[];
    url?: string[];
    target?: '_blank' | '_self' | '_parent' | '_top';
    routerLinkActiveOptions?: { [key: string]: any };
    items?: MenuItem[];
    separator?: boolean;
    visible?: boolean;
    disabled?: boolean;
    command?: (event?: any) => void;
    class?: string;
    style?: string;
    styleClass?: string;
    id?: string;
    urlTarget?: '_blank' | '_self' | '_parent' | '_top';
}

@Component({
    selector: '[app-menu]',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule],
    template: ` <ul class="layout-menu" #menuContainer>
        <ng-container *ngFor="let item of model; let i = index">
            <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
            <li *ngIf="item.separator" class="menu-separator"></li>
        </ng-container>
    </ul>`,
    host: {
        class: 'layout-menu-container'
    }
})
export class AppMenu implements OnInit {
    private el: ElementRef = inject(ElementRef);
    private kc = inject(Keycloak);

    @ViewChild('menuContainer') menuContainer!: ElementRef;

    model: MenuItem[] = [];

    private routeRoles!: Map<string, string[]>;

    ngOnInit(): void {
        this.routeRoles = buildRouteRolesMap(appRoutes);

        this.model = [
            {
                label: 'Produto 01',
                icon: 'pi pi-cog',
                items: [
                    {
                        label: 'Mapeamento Urbano',
                        icon: 'pi pi-fw pi-building',
                        visible: this.canAccess('mapeamento-urbano'),
                        routerLink: ['/mapeamento-urbano']
                    }
                ]
            },
            {
                label: 'APM',
                icon: 'pi pi-map-marker',
                items: [
                    {
                        label: 'Áreas Públicas do Município',
                        icon: 'pi pi-fw pi-map',
                        visible: this.canAccess('apm'),
                        routerLink: ['/apm']
                    },
                    {
                        label: 'Diários Oficiais das APMs',
                        icon: 'pi pi-fw pi-file-edit',
                        visible: this.canAccess('apm-do'),
                        routerLink: ['/apm-do']
                    },
                    {
                        label: 'Extração de APMs - Armário',
                        icon: 'pi pi-fw pi-file-import',
                        visible: this.canAccess('apm-extracao'),
                        routerLink: ['/apm-extracao']
                    },
                    {
                        label: 'Monitoramento Temporal das APMs',
                        icon: 'pi pi-fw pi-chart-line',
                        visible: this.canAccess('monitoramento'),
                        routerLink: ['/monitoramento']
                    }
                ]
            },
            {
                label: 'Produto 03',
                icon: 'pi pi-calculator',
                items: [
                    {
                        label: 'Observatório de Mercado',
                        icon: 'pi pi-fw pi-home',
                        visible: this.canAccess('iptu'),
                        routerLink: ['/iptu']
                    },
                    {
                        label: 'Avaliacoes de Resultados',
                        icon: 'pi pi-fw pi-star',
                        visible: this.canAccess('feedbacks'),
                        routerLink: ['/feedbacks/lista']
                    }
                ]
            },
            {
                label: 'Gestão de Análises',
                icon: 'pi pi-chart-bar',
                items: [
                    {
                        label: 'Cadastro de Análises',
                        icon: 'pi pi-fw pi-folder-open',
                        visible: this.canAccess('analise'),
                        routerLink: ['/analise/lista']
                    },
                    {
                        label: 'Nova Análise',
                        icon: 'pi pi-fw pi-plus',
                        visible: this.canAccess('analise'),
                        routerLink: ['/analise/novo']
                    }
                ]
            }
        ];

        // Oculta grupos sem itens visíveis
        for (const group of this.model) {
            if (group.items?.length) {
                group.visible = group.items.some(item => item.visible !== false);
            }
        }
    }

    private canAccess(path: string): boolean {
        const roles = this.routeRoles.get(path);
        if (!roles) return true;
        const parsed = this.kc.tokenParsed as any;
        return roles.some((required) => {
            const [resource, role] = required.split('.');
            // Prefixo reservado 'realm.' consulta as realm roles do token
            if (resource === 'realm') {
                return parsed?.realm_access?.roles?.includes(role) ?? false;
            }
            return parsed?.resource_access?.[resource]?.roles?.includes(role) ?? false;
        });
    }
}
